import React, { useContext, useEffect, useState } from "react";

import { useMutation } from "@apollo/client";
import { login, loginVariables } from "@layout/modals/graphql/__generated__/login";
import { LOGIN_MUTATION } from "@layout/modals/graphql/LOGIN_MUTATION";
import { setAuthParamToURL } from "@layout/modals/utils";
import { useLocation } from "@reach/router";
import { navigate } from "gatsby";
import _uniqueId from "lodash/uniqueId";

import { getRedirectUrlSessionStorage, setRedirectUrlSessionStorage } from "@/account/redirect";
import { setAccessToken, setCookieAccepted, setRefreshToken } from "@/account/user";
import { isPasswordValid } from "@/account/utils";
import { isEmailValid } from "@/components/common/Inputs/emailInput/utils";
import UserContext from "@/contexts/User/UserContext";
import { trimAndLowerCase } from "@/utils/stringUtils";
import { Button, ButtonColorEnum, ButtonSizeEnum } from "@components/common/button";
import PasswordInput from "@components/common/passwordInput";

import { EXPERT_AGREEMENT_LINKS } from "./constants";

import "./styles.scss";

/**
 * Form for experts login
 * @returns
 */
const OldFormModal = () => {
  const { setUserID, setIsCookiePermitted } = useContext(UserContext);
  const location = useLocation();
  const [loginUser, { data: loginData, loading: loginLoading, error: loginError }] = useMutation<
    login,
    loginVariables
  >(LOGIN_MUTATION, {
    errorPolicy: "all", // todo: make general behaviour for network errors
  });

  const [email, setEmail] = useState<string>("");
  const [isEmailTouched, setIsEmailTouched] = useState<boolean>(false);
  const [password, setPassword] = useState<string>("");
  const [isPasswordBeTouched, setIsPasswordBeTouched] = useState<boolean>(false);
  const [isLoginError, setIsLoginError] = useState<boolean>(false);
  const [inputUniqueId] = useState(_uniqueId());
  const [showAgreement, setShowAgreement] = useState<boolean>(false);
  const [isAgreementAccepted, setIsAgreementAccepted] = useState<boolean>(false);

  const handleSubmit = () => {
    setCookieAccepted(true);
    setIsCookiePermitted(true);
    if (!isEmailValid(email) || !isPasswordValid(password, false)) {
      return;
    }
    if (showAgreement && !isAgreementAccepted) {
      return;
    }
    loginUser({
      variables: {
        email: trimAndLowerCase(email),
        password,
        acceptAgreement: isAgreementAccepted,
      },
    });
  };

  const onKeyDownHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.code === "Enter" || event.code === "NumpadEnter") {
      event.preventDefault();
      setIsEmailTouched(true);
      setIsPasswordBeTouched(true);
      handleSubmit();
    }
  };

  useEffect(() => {
    if (loginData && !loginLoading && !loginError) {
      if (isLoginError) {
        setIsLoginError(false);
      }

      if (loginData.login.needAgreement) {
        setShowAgreement(true);
        return;
      }

      if (!loginData.login.tokens) {
        return;
      }

      setUserID(loginData.login.userID);
      setAccessToken(loginData.login.tokens.accessToken);
      setRefreshToken(loginData.login.tokens.refreshToken);

      setAuthParamToURL(location, null);

      if (getRedirectUrlSessionStorage()) {
        const redirectUrl = getRedirectUrlSessionStorage() || "";
        navigate(redirectUrl);
        setRedirectUrlSessionStorage("");
      }
    } else if (loginError) {
      setIsLoginError(true); // todo: check error code
    }
  }, [loginData, loginLoading, loginError, isLoginError, setUserID, location]);

  return (
    <div className="old-form-modal">
      <div className="input-group">
        <div className="input-group__item">
          <label htmlFor={`email${inputUniqueId}`}>Email</label>
          {/* tofo: reuse EmailBlock */}
          <input
            type="email"
            className={isEmailTouched && !isEmailValid(email) ? " error" : ""}
            name="email"
            id={`email${inputUniqueId}`}
            aria-invalid="true"
            value={email}
            autoFocus
            onChange={(event) =>
              setEmail(event.target.value)}
            onBlur={() =>
              setIsEmailTouched(true)}
            onKeyDown={(e) =>
              onKeyDownHandler(e)}
            disabled={loginLoading}
            placeholder="maria@mail.ru"
          />
        </div>
        {isEmailTouched && !email && (
          <div className="input-group__error" role="alert">
            Укажите свой адрес электронной почты
          </div>
        )}
        {isEmailTouched && email && !isEmailValid(email) && (
          <div className="input-group__error" role="alert">
            Введите верный email. Например, maria@gmail.com
          </div>
        )}
      </div>
      <div className="input-group">
        <div className="input-group__item">
          <label htmlFor={`password${inputUniqueId}`}>Пароль</label>
          <PasswordInput
            className={isPasswordBeTouched && !isPasswordValid(password, false) ? " error" : ""}
            name="password"
            id={`password${inputUniqueId}`}
            aria-invalid="true"
            value={password}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setPassword(e.target.value)}
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) =>
              onKeyDownHandler(e)}
            onBlur={() =>
              setIsPasswordBeTouched(true)}
            disabled={loginLoading}
          />
        </div>
        {isPasswordBeTouched && !password && (
          <div className="input-group__error" role="alert">
            Укажите пароль. Не менее 6 символов
          </div>
        )}
        {isPasswordBeTouched && password && password.length < 6 && (
          <div className="input-group__error" role="alert">
            Слишком короткий пароль. Он должен содержать не&nbsp;менее 6&nbsp;символов
          </div>
        )}
        {loginError && (
          <div className="input-group__error" role="alert">
            Неверный логин и/или пароль
          </div>
        )}
      </div>
      {showAgreement && (
        <div
          className="expert-agreement"
          onClick={() => {
            if (!loginLoading) {
              setIsAgreementAccepted((val) =>
                !val);
            }
          }}
        >
          <input
            type="checkbox"
            className="expert-agreement__checkbox"
            checked={isAgreementAccepted}
            disabled={loginLoading}
            readOnly
          />
          <span className="expert-agreement__text">
            Я ознакомлен(а) и принимаю условия
            {" "}
            <a
              href={EXPERT_AGREEMENT_LINKS.userAgreement}
              target="_blank"
              rel="noreferrer"
              onClick={(e) =>
                e.stopPropagation()}
            >
              Пользовательского соглашения для Эксперта
            </a>
            , подтверждаю обязанность соблюдать
            {" "}
            <a
              href={EXPERT_AGREEMENT_LINKS.serviceRules}
              target="_blank"
              rel="noreferrer"
              onClick={(e) =>
                e.stopPropagation()}
            >
              Правила оказания услуг
            </a>
            ,
            {" "}
            <a
              href={EXPERT_AGREEMENT_LINKS.privacyPolicy}
              target="_blank"
              rel="noreferrer"
              onClick={(e) =>
                e.stopPropagation()}
            >
              Политику конфиденциальности
            </a>
            {" "}
            и условия Договора об оказании консультационных услуг с использованием
            Платформы Лунаро
          </span>
        </div>
      )}
      <Button
        className="auth__submit"
        type="submit"
        text="Войти"
        size={ButtonSizeEnum.Middle}
        color={ButtonColorEnum.Dark}
        onClick={handleSubmit}
        disabled={
          !isEmailValid(email)
          || !isPasswordValid(password, true)
          || (showAgreement && !isAgreementAccepted)
        }
      />
    </div>
  );
};

export default OldFormModal;
